import { useEffect, useState, useCallback, useRef } from 'react';
import { api } from './apiClient.js';

export function useApi(path, { query, deps = [], skip = false } = {}) {
  const [state, setState] = useState({
    data: null,
    loading: !skip,
    error: null,
    loadedAt: null,
    // Identity of the query whose response currently sits in `data`. Lets a
    // consumer detect that the retained data belongs to a *previous* query
    // while a new one is loading (PRIME-SEARCH-DEF-001, ticket §7 lead 1).
    loadedKey: null,
  });
  const queryKey = JSON.stringify(query || {});
  const reqRef = useRef(0);

  const fetchNow = useCallback(() => {
    if (skip || !path) return Promise.resolve();
    const seq = ++reqRef.current;
    // Capture the key this request belongs to so the committed response can be
    // stamped with its own identity, even if the hook re-keys before it lands.
    const key = queryKey;
    setState((s) => ({ ...s, loading: true, error: null }));
    return api
      .get(path, { query })
      .then((res) => {
        if (seq === reqRef.current)
          setState({ data: res, loading: false, error: null, loadedAt: new Date(), loadedKey: key });
      })
      .catch((err) => {
        if (seq === reqRef.current)
          setState((s) => ({ data: null, loading: false, error: err, loadedAt: s.loadedAt, loadedKey: key }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, queryKey, skip]);

  useEffect(() => {
    fetchNow();
    const requestRef = reqRef;
    return () => {
      // bump sequence so any in-flight result is discarded on unmount/re-key
      requestRef.current++;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, queryKey, skip, ...deps]);

  // Re-render-safe update primitives. `setData` accepts either a new value or
  // a `(prev) => next` updater function applied to the current `data` field.
  // `refresh()` re-invokes the GET; useful after a mutation when the response
  // does not carry the full updated record.
  const setData = useCallback((updater) => {
    setState((s) => {
      const nextData = typeof updater === 'function' ? updater(s.data) : updater;
      return { ...s, data: nextData };
    });
  }, []);

  // `stale` is derived (not stored) so it is correct on the *first* render after
  // a re-key, before the load effect runs and flips `loading`. It is true only
  // while active data belongs to a query other than the one now requested.
  const stale = !skip && state.data != null && state.loadedKey !== queryKey;

  return { ...state, stale, setData, refresh: fetchNow };
}

export function useMutation() {
  const [state, setState] = useState({ running: false, error: null, data: null });
  const run = useCallback(async (fn) => {
    setState({ running: true, error: null, data: null });
    try {
      const data = await fn();
      setState({ running: false, error: null, data });
      return data;
    } catch (err) {
      setState({ running: false, error: err, data: null });
      throw err;
    }
  }, []);
  return { ...state, run };
}
